import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  LinearProgress,
  Checkbox,
  Avatar,
  IconButton,
  TextField,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import { FaFileExcel, FaUpload, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import useApiEp from "../../hooks/useApiEp";

function ContactsTabs({
  campaignData,
  setCampaignData,
  tabState,
  setTabState,
  isDragged,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileUpload,
  downloadTemplate,
  uploadProgress,
  selectedCount,
  indeterminate,
  allSelected,
  handleSelectAll,
  searchQuery,
  setSearchQuery,
  filteredContacts,
  toggleSelect,
  removeContact,
  contactGroupName,
  campaignGroupName,
}) {
  const set = (patch) => setTabState((prev) => ({ ...prev, ...patch }));

  const {
    tabValue,
    selectedList,
    dbColumns,
    dbStartIndex,
    dbEndIndex,
    pendingGroup,
    groupSearchQuery,
    groupStartIndex,
    groupEndIndex,
  } = tabState;

  // ── Derived: is campaignGroupName a valid non-empty string? ──────────────
  const isCampaignGroupLocked =
    typeof campaignGroupName === "string" && campaignGroupName.trim() !== "";

  // ── API: Tab 1 (Database) ────────────────────────────────────────────────
  const { data: dbLists } = useApiEp("/user/get-my-assigned-dbs");

  const {
    data: filterCount,
    isLoading: loadingFilterCount,
    isError: isErrorFilterCount,
    error: errorFilterCount,
  } = useApiEp(
    selectedList
      ? `/database/get-list-count?listId=${selectedList}${
          dbStartIndex ? `&startIndex=${dbStartIndex}` : ""
        }${dbEndIndex ? `&endIndex=${dbEndIndex}` : ""}`
      : "",
    !!selectedList
  );

  // ── API: Tab 3 (Contacts List) ───────────────────────────────────────────
  // Always fetch when locked so the tab is immediately usable
  const {
    data: groupList,
    isLoading: loadingGroupList,
    isError: isErrorGroupList,
  } = useApiEp("/user/group-list", isCampaignGroupLocked || tabValue === 3);

  const {
    data: groupPreview,
    isLoading: loadingGroupPreview,
    isError: isErrorGroupPreview,
    error: errorGroupPreview,
  } = useApiEp(
    pendingGroup ? `/user/get-contacts-preview-group/${pendingGroup}` : "",
    !!pendingGroup
  );

  // ── Helpers ──────────────────────────────────────────────────────────────
  const checkForDuplicates = (newContacts) => {
    const existingPhones = new Set(campaignData.contacts.map((c) => c.phone));
    return newContacts.filter((contact) => {
      if (existingPhones.has(contact.phone)) {
        toast.warn(`Duplicate phone number ${contact.phone} skipped`);
        return false;
      }
      return true;
    });
  };

  // When locked: only the matching group is shown; otherwise normal search filter
  const filteredGroupList = isCampaignGroupLocked
    ? groupList?.filter((g) => g === contactGroupName)
    : groupList?.filter((g) =>
        g.toLowerCase().includes(groupSearchQuery.toLowerCase())
      );

  // ── Tab 1 handlers ───────────────────────────────────────────────────────
  const handleChangeList = (event) => {
    const listId = event.target.value;
    const list = dbLists?.find((l) => l._id === listId);
    set({ selectedList: listId, dbColumns: list ? list.columns : [] });
    setCampaignData((prev) => ({ ...prev, selectedList: listId }));
  };

  const handleConfirmDbSelection = () => {
    if (!selectedList) {
      toast.error("Please select a list first");
      return;
    }
    setCampaignData((prev) => ({
      ...prev,
      contacts: [],
      selectedGroup: selectedList,
      startIndex: dbStartIndex || undefined,
      endIndex: dbEndIndex || undefined,
      dbGroup: "database",
    }));
    toast.success("Database list selected for campaign");
    set({ selectedList: "", dbColumns: [], dbStartIndex: "", dbEndIndex: "" });
  };

  // ── Tab 2 (Quick List) handler ───────────────────────────────────────────
  const handleAddQuickList = (quickListText, setQuickListText) => {
    if (!quickListText.trim()) {
      alert("Please enter phone numbers");
      return;
    }
    const phoneNumbers = quickListText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (phoneNumbers.length === 0) {
      alert("No valid phone numbers found");
      return;
    }
    const newContacts = checkForDuplicates(
      phoneNumbers.map((phone, index) => ({
        id: Date.now() + index,
        name: "",
        phone,
        selected: true,
      }))
    );
    if (newContacts.length === 0) {
      toast.error("No new contacts added (all were duplicates)");
      return;
    }
    setCampaignData((prev) => ({
      ...prev,
      contacts: [...prev.contacts, ...newContacts],
      selectedGroup: "",
    }));
    setQuickListText("");
    toast.success(`Added ${newContacts.length} contacts from quick list`);
  };

  // ── Tab 3 handlers ───────────────────────────────────────────────────────
  const handleSelectPendingGroup = (groupName) =>
    set({
      pendingGroup: groupName,
      groupSearchQuery: "",
      groupStartIndex: "",
      groupEndIndex: "",
    });

  const handleClearPendingGroup = () =>
    set({
      pendingGroup: "",
      groupSearchQuery: "",
      groupStartIndex: "",
      groupEndIndex: "",
    });

  const handleConfirmGroupSelection = () => {
    if (!pendingGroup) {
      toast.error("Please select a group first");
      return;
    }
    setCampaignData((prev) => ({
      ...prev,
      contacts: [],
      selectedGroup: pendingGroup,
      startIndex: groupStartIndex || undefined,
      endIndex: groupEndIndex || undefined,
      dbGroup: "contacts",
    }));
    toast.success(`Contact group "${pendingGroup}" selected for campaign`);
    handleClearPendingGroup();
  };

  // ── Quick List local state ────────────────────────────────────────────────
  const [quickListText, setQuickListText] = useState("");

  // ── Tab definitions (hidden when locked) ─────────────────────────────────
  // When isCampaignGroupLocked, we render ONLY the Contacts List tab.
  // We keep its index as 3 to avoid refactoring tabValue logic, but we
  // show a single-tab <Tabs> that is visually inert.

  return (
    <Box sx={{ width: "100%" }}>
      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      {isCampaignGroupLocked ? (
        // Single, non-interactive tab header when a campaign group is locked in
        <Tabs value={0}>
          <Tab label="Contacts List" />
        </Tabs>
      ) : (
        <Tabs
          value={tabValue}
          onChange={(e, v) => {
            set({ tabValue: v });
            handleClearPendingGroup();
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Import from Excel" />
          <Tab label="Use Database" />
          <Tab label="Quick List" />
          <Tab label="Contacts List" />
        </Tabs>
      )}

      {/* ── Tab 0: Import from Excel (hidden when locked) ─────────────────── */}
      {!isCampaignGroupLocked && tabValue === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Upload your contacts via Excel file
          </Typography>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-upload").click()}
            sx={{
              border: "2px dashed #ccc",
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              borderRadius: 2,
              bgcolor: isDragged ? "grey.100" : "background.paper",
            }}
          >
            <input
              id="file-upload"
              type="file"
              accept=".xlsx, .xls"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleFileUpload(file, checkForDuplicates);
              }}
            />
            <FaFileExcel
              size={48}
              color="#217346"
              style={{ marginBottom: 16 }}
            />
            <Typography variant="h6" gutterBottom>
              {campaignData.uploadedFile
                ? "File Uploaded!"
                : "Drop your Excel file here"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Supports .xlsx and .xls files with phone numbers
            </Typography>
            <Button variant="contained" startIcon={<FaUpload />} sx={{ mt: 2 }}>
              Browse Files
            </Button>
            <Button
              variant="outlined"
              onClick={downloadTemplate}
              sx={{ mt: 2, ml: 2 }}
            >
              Download Template
            </Button>
          </Box>
          {uploadProgress > 0 && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {uploadProgress === 100 ? "Uploaded" : "Uploading..."}{" "}
                {uploadProgress}%
              </Typography>
            </Box>
          )}
          {campaignData.contacts.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6" gutterBottom>
                  Uploaded Contacts ({selectedCount} selected /{" "}
                  {campaignData.contacts.length} total)
                </Typography>
                <Box display="flex" alignItems="center">
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  <Typography>Select All</Typography>
                </Box>
              </Box>
              <TextField
                fullWidth
                placeholder="Search by phone number"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 400, overflow: "auto" }}
              >
                {filteredContacts.map((contact) => (
                  <Box
                    key={contact.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1, p: 1, bgcolor: "grey.50", borderRadius: 1 }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Checkbox
                        checked={contact.selected}
                        onChange={() => toggleSelect(contact.id)}
                      />
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
                      >
                        {contact.name ? contact.name.charAt(0) : "?"}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">
                          {contact.name || "Unknown"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {contact.phone}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeContact(contact.id)}
                    >
                      <FaTrash color="#f44336" />
                    </IconButton>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* ── Tab 1: Use Database (hidden when locked) ─────────────────────── */}
      {!isCampaignGroupLocked && tabValue === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Select a Saved List from Database
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="list-select-label">Contact List</InputLabel>
            <Select
              labelId="list-select-label"
              value={selectedList}
              label="Contact List"
              onChange={handleChangeList}
            >
              {dbLists?.map((list) => (
                <MenuItem key={list._id} value={list._id}>
                  {list.name} ({list.columns.length} columns)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Start Index"
              type="number"
              size="small"
              value={dbStartIndex}
              onChange={(e) => set({ dbStartIndex: e.target.value })}
              sx={{ flex: 1 }}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="End Index"
              type="number"
              size="small"
              value={dbEndIndex}
              onChange={(e) => set({ dbEndIndex: e.target.value })}
              sx={{ flex: 1 }}
              inputProps={{ min: dbStartIndex || 0 }}
            />
          </Box>
          {loadingFilterCount && (
            <Typography variant="body2" color="textSecondary">
              Loading contact count...
            </Typography>
          )}
          {isErrorFilterCount && (
            <Typography variant="body2" color="error">
              Error fetching contact count: {errorFilterCount?.message}
            </Typography>
          )}
          {filterCount && (
            <Typography variant="subtitle1" gutterBottom>
              Total Contacts in List: {filterCount.count}
            </Typography>
          )}
          {dbColumns.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Column Preview:
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, maxHeight: 300, overflow: "auto" }}
              >
                {dbColumns.map((column, idx) => (
                  <Box
                    key={idx}
                    sx={{ mb: 1, p: 1, bgcolor: "grey.50", borderRadius: 1 }}
                  >
                    <Typography variant="body1">{column}</Typography>
                  </Box>
                ))}
              </Paper>
              <Button
                variant="contained"
                fullWidth
                onClick={handleConfirmDbSelection}
              >
                Confirm Selection
              </Button>
            </>
          )}
          {campaignData.selectedGroup &&
            campaignData.dbGroup === "database" && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "success.50",
                  border: "1px solid",
                  borderColor: "success.light",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle1" color="success.dark">
                  ✓ Database list <strong>{campaignData.selectedGroup}</strong>{" "}
                  confirmed
                  {campaignData.startIndex !== undefined &&
                  campaignData.endIndex !== undefined
                    ? ` · Indices ${campaignData.startIndex} – ${campaignData.endIndex}`
                    : ""}
                </Typography>
              </Box>
            )}
        </Box>
      )}

      {/* ── Tab 2: Quick List (hidden when locked) ───────────────────────── */}
      {!isCampaignGroupLocked && tabValue === 2 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Add Quick List (Phone Numbers only)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Paste phone numbers below, one per line.
          </Typography>
          <TextField
            multiline
            rows={8}
            fullWidth
            placeholder={"eg. 9876543210\n9123456789\n9988776655"}
            value={quickListText}
            onChange={(e) => setQuickListText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleAddQuickList(quickListText, setQuickListText)}
          >
            Add Quick List
          </Button>
          {campaignData.contacts.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                All Contacts ({selectedCount} selected /{" "}
                {campaignData.contacts.length} total)
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 300, overflow: "auto" }}
              >
                {campaignData.contacts.map((contact) => (
                  <Box
                    key={contact.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1, p: 1, bgcolor: "grey.50", borderRadius: 1 }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
                      >
                        {contact.name ? contact.name.charAt(0) : "?"}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">
                          {contact.name || "Unknown"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {contact.phone}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeContact(contact.id)}
                    >
                      <FaTrash color="#f44336" />
                    </IconButton>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* ── Tab 3: Contacts List (always visible when locked, else tabValue===3) */}
      {(isCampaignGroupLocked || tabValue === 3) && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Select a Contact Group
          </Typography>

          {/* Step 1 — group picker */}
          {!pendingGroup && (
            <>
              {/* Search bar hidden when locked (only one group shown anyway) */}
              {!isCampaignGroupLocked && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search groups..."
                  value={groupSearchQuery}
                  onChange={(e) => set({ groupSearchQuery: e.target.value })}
                  sx={{ mb: 2 }}
                />
              )}
              {loadingGroupList && (
                <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="textSecondary">
                    Loading groups...
                  </Typography>
                </Box>
              )}
              {isErrorGroupList && (
                <Typography variant="body2" color="error">
                  Failed to load groups. Please try again.
                </Typography>
              )}
              {!loadingGroupList && filteredGroupList?.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No groups found.
                </Typography>
              )}
              <Paper
                variant="outlined"
                sx={{ maxHeight: 320, overflow: "auto" }}
              >
                <List disablePadding>
                  {filteredGroupList?.map((group) => (
                    <ListItem key={group} disablePadding divider>
                      <ListItemButton
                        onClick={() => handleSelectPendingGroup(group)}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: "secondary.main",
                            mr: 2,
                            fontSize: 14,
                          }}
                        >
                          {group.charAt(0).toUpperCase()}
                        </Avatar>
                        <ListItemText primary={group} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </>
          )}

          {/* Step 2 — preview, range, confirm */}
          {pendingGroup && (
            <>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <Chip
                  label={pendingGroup}
                  // Hide the delete (×) button when locked — user can't change group
                  onDelete={
                    isCampaignGroupLocked ? undefined : handleClearPendingGroup
                  }
                  color="primary"
                />
                {!isCampaignGroupLocked && (
                  <Typography variant="body2" color="textSecondary">
                    — click × to pick a different group
                  </Typography>
                )}
              </Box>

              {loadingGroupPreview && (
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="textSecondary">
                    Loading preview...
                  </Typography>
                </Box>
              )}
              {isErrorGroupPreview && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  Error: {errorGroupPreview?.message}
                </Typography>
              )}

              {groupPreview && (
                <>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Total contacts in group:{" "}
                    <strong>{groupPreview.totalCount}</strong>
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                      label="Start Index"
                      type="number"
                      size="small"
                      value={groupStartIndex}
                      onChange={(e) => set({ groupStartIndex: e.target.value })}
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0, max: groupPreview.totalCount - 1 }}
                      helperText="Leave blank for beginning"
                    />
                    <TextField
                      label="End Index"
                      type="number"
                      size="small"
                      value={groupEndIndex}
                      onChange={(e) => set({ groupEndIndex: e.target.value })}
                      sx={{ flex: 1 }}
                      inputProps={{
                        min: groupStartIndex || 0,
                        max: groupPreview.totalCount - 1,
                      }}
                      helperText="Leave blank for all contacts"
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 1 }}
                  >
                    Sample preview (up to 2 contacts):
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
                    {groupPreview.contacts.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ p: 1, textAlign: "center" }}
                      >
                        No preview contacts available.
                      </Typography>
                    ) : (
                      groupPreview.contacts.map((contact, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            sx={{ p: 1, bgcolor: "grey.50", borderRadius: 1 }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "primary.main",
                              }}
                            >
                              {typeof contact?.name === "string" &&
                              contact.name.trim() !== ""
                                ? contact.name.charAt(0)
                                : "?"}
                            </Avatar>
                            <Box>
                              <Typography variant="body1">
                                {contact.name || "Unknown"}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {contact.phoneNumber}
                              </Typography>
                            </Box>
                          </Box>
                        </React.Fragment>
                      ))
                    )}
                  </Paper>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleConfirmGroupSelection}
                    disabled={groupPreview.totalCount === 0}
                  >
                    Confirm — Use &quot;{pendingGroup}&quot;{" "}
                    {groupStartIndex && groupEndIndex
                      ? `(Indices ${groupStartIndex} – ${groupEndIndex})`
                      : `(${groupPreview.totalCount} contacts)`}
                  </Button>
                </>
              )}
            </>
          )}

          {/* Confirmed summary banner */}
          {campaignData.selectedGroup &&
            campaignData.dbGroup === "contacts" && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "success.50",
                  border: "1px solid",
                  borderColor: "success.light",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle1" color="success.dark">
                  ✓ Group <strong>{campaignData.selectedGroup}</strong>{" "}
                  confirmed
                  {campaignData.startIndex !== undefined &&
                  campaignData.endIndex !== undefined
                    ? ` · Indices ${campaignData.startIndex} – ${campaignData.endIndex}`
                    : " · All contacts"}
                </Typography>
              </Box>
            )}
        </Box>
      )}
    </Box>
  );
}

export default ContactsTabs;
